import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useApi } from '~/composables/useApi'
import {
  extractCategories,
  extractCustomCategories,
  getBrandCategorySlug,
  groupBrandsByCategory,
  groupBrandsByCustomCategory,
  isCustomCategoryId,
  mapCatalogItems,
  mapCustomCategoryItems,
} from '~/utils/brand'

function normalizeCategoryId(categoryId) {
  if (categoryId == null || categoryId === '') {
    return null
  }

  return String(categoryId)
}

function parseFindBrandOptions(categoryIdOrOptions) {
  if (categoryIdOrOptions != null && typeof categoryIdOrOptions === 'object') {
    return {
      categoryId: categoryIdOrOptions.categoryId ?? null,
      categorySlug: categoryIdOrOptions.categorySlug ?? null,
    }
  }

  return {
    categoryId: categoryIdOrOptions ?? null,
    categorySlug: null,
  }
}

export const useCatalogStore = defineStore('catalog', () => {
  const selectedCategoryId = ref(null)

  const catalogItems = ref([])
  const customCategoryItems = ref([])

  const pending = ref(true)
  const error = ref(null)
  const initialized = ref(false)

  let inflight = null

  const catalogBrands = computed(() => mapCatalogItems(catalogItems.value))
  const customCategoryBrands = computed(() => mapCustomCategoryItems(customCategoryItems.value))
  const categories = computed(() => [
    ...extractCustomCategories(customCategoryItems.value),
    ...extractCategories(catalogItems.value),
  ])

  const homeCatalogSections = computed(() => [
    ...groupBrandsByCustomCategory(customCategoryBrands.value),
    ...groupBrandsByCategory(catalogBrands.value),
  ])

  const filteredCatalogBrands = computed(() => {
    const selected = selectedCategoryId.value

    if (selected === null) {
      return catalogBrands.value
    }

    if (isCustomCategoryId(selected)) {
      return customCategoryBrands.value.filter(
        (brand) => brand.customCategoryKey === selected,
      )
    }

    return catalogBrands.value.filter((brand) => brand.categoryId === selected)
  })

  async function fetchCatalog() {
    if (inflight) {
      return inflight
    }

    pending.value = true
    error.value = null

    inflight = (async () => {
      try {
        const api = useApi()
        const response = await api('/api/v1/web-game')
        catalogItems.value = response.all ?? []
        customCategoryItems.value = response.custom_category ?? []
      } catch (err) {
        error.value = err
      } finally {
        pending.value = false
        initialized.value = true
        inflight = null
      }
    })()

    return inflight
  }

  async function ensureCatalog() {
    if (!initialized.value) {
      await fetchCatalog()
    }
  }

  function selectCategory(categoryId) {
    selectedCategoryId.value = categoryId
  }

  function findBrandById(id) {
    return (
      catalogBrands.value.find((brand) => brand.id === id)
      ?? customCategoryBrands.value.find((brand) => brand.id === id)
    )
  }

  function findBrandBySlug(slug, categoryIdOrOptions) {
    const normalizedSlug = String(slug ?? '').trim().toLowerCase()
    if (!normalizedSlug) {
      return undefined
    }

    const { categoryId, categorySlug } = parseFindBrandOptions(categoryIdOrOptions)
    const wantedCategoryId = normalizeCategoryId(categoryId)
    const wantedCategorySlug = typeof categorySlug === 'string' && categorySlug.trim() !== ''
      ? categorySlug.trim().toLowerCase()
      : null

    const matches = []
    for (const pool of [catalogBrands.value, customCategoryBrands.value]) {
      for (const brand of pool) {
        if (brand.slug === normalizedSlug) {
          matches.push(brand)
        }
      }
    }

    const matchesCategoryId = (brand) =>
      wantedCategoryId != null
      && normalizeCategoryId(brand.categoryId) === wantedCategoryId

    const matchesCategorySlug = (brand) =>
      wantedCategorySlug != null
      && getBrandCategorySlug(brand) === wantedCategorySlug

    if (wantedCategoryId != null && wantedCategorySlug != null) {
      const exact = matches.find((brand) =>
        matchesCategoryId(brand) && matchesCategorySlug(brand),
      )
      if (exact) {
        return exact
      }
    }

    if (wantedCategorySlug != null) {
      const bySlug = matches.find(matchesCategorySlug)
      if (bySlug) {
        return bySlug
      }
    }

    if (wantedCategoryId != null) {
      const byId = matches.find(matchesCategoryId)
      if (byId) {
        return byId
      }
    }

    if (wantedCategoryId != null || wantedCategorySlug != null) {
      return undefined
    }

    return matches[0]
  }

  return {
    selectedCategoryId,
    pending,
    error,
    initialized,
    catalogBrands,
    categories,
    homeCatalogSections,
    filteredCatalogBrands,
    fetchCatalog,
    ensureCatalog,
    refresh: fetchCatalog,
    selectCategory,
    findBrandById,
    findBrandBySlug,
  }
})
